import _ from 'lodash'
import { useMemo, useState } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Row, createColumnHelper } from '@tanstack/react-table'

import { Box, Checkbox, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'
import ChipButton from '@/components/ui/buttons/ChipButton'
import Table from '@/components/ui/tables/Table'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { ContractFormValues } from './ContractForm'

type AssignedLicenseData = {
  id: string
  license: string
  quantity: number
  licensees?: string[]
}

type AssignedLicensesTableProps = Readonly<{
  disableAssign?: boolean
  onDelete?: (id: string) => void
  readOnly?: boolean
  staffOptions?: AutocompleteOption[]
}>

const columnHelpers = createColumnHelper<AssignedLicenseData>()

export default function AssignedLicensesTable(props: AssignedLicensesTableProps) {
  const { disableAssign = false, onDelete, readOnly = false, staffOptions = [] } = props
  const { t } = useTranslation()

  const { control, setValue } = useFormContext<ContractFormValues>()
  const {
    field: { value: licenseUsagesValue },
  } = useController({ control, name: 'licenseUsages' })

  const [drawOpen, setDrawerOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<Row<AssignedLicenseData>>()

  const data = useMemo(
    () =>
      licenseUsagesValue.map(({ license: { id, name }, quantity, licensees }) => ({
        id,
        license: name,
        quantity,
        licensees,
      })),
    [licenseUsagesValue]
  )

  const columns = useMemo(
    () =>
      _.compact([
        columnHelpers.accessor('license', {
          header: t('table.licenses.header.name').toString(),
        }),
        columnHelpers.display({
          id: 'quantity',
          header: t('field.quantity.label').toString(),
          cell: ({ row }) =>
            readOnly ? (
              <>{row.original.quantity}</>
            ) : (
              <Box pt={1} pb={1}>
                <TextInputController control={control} fullWidth name={`licenseUsages.${row.index}.quantity`} type="number" />
              </Box>
            ),
        }),
        !disableAssign &&
          columnHelpers.display({
            id: 'licensees',
            header: t('table.licenses.header.licensees').toString(),
            cell: ({ row }) => (
              <Controller control={control} name={`licenseUsages.${row.index}.licensees`} render={({ field }) => <>{field.value?.length ?? 0}</>} />
            ),
          }),
        !readOnly &&
          columnHelpers.display({
            id: 'actions',
            cell: ({ row, table }) => (
              <Stack direction="row" justifyContent="end" spacing={1}>
                {!disableAssign && (
                  <ChipButton
                    onClick={() => {
                      setSelectedRow(row)
                      setDrawerOpen(true)
                    }}
                  >
                    <AssignmentIndRoundedIcon />
                  </ChipButton>
                )}
                <ChipButton color="error" onClick={() => table.options.meta?.delete?.(row.original.id)}>
                  <DeleteRoundedIcon />
                </ChipButton>
              </Stack>
            ),
          }),
      ]),
    [control, disableAssign, readOnly, t]
  )

  if (!data.length) return null

  return (
    <>
      <Table columns={columns} data={data} meta={{ delete: (id) => onDelete?.(id) }} />
      <Drawer
        anchor="right"
        open={drawOpen}
        onClose={() => {
          setSelectedRow(undefined)
          setDrawerOpen(false)
        }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 10 }}
      >
        {!!selectedRow && (
          <Stack direction="column" sx={{ height: '100%', minWidth: { xs: '360px', sm: '500px', md: '600px' }, p: '16px 32px' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography component="h2" variant="h2">
                {selectedRow.original.license}
              </Typography>
              <IconButton
                onClick={() => {
                  setSelectedRow(undefined)
                  setDrawerOpen(false)
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
            <Box flex={1} py={3}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {staffOptions.map((option) => {
                  return (
                    <Controller
                      key={option.value}
                      control={control}
                      name={`licenseUsages.${selectedRow.index}.licensees`}
                      render={({ field }) => (
                        <ListItemButton
                          disabled={selectedRow.original.quantity === field.value?.length && !field.value?.includes(option.value)}
                          role={undefined}
                          onClick={() => {
                            const licensees = field.value ?? []
                            const currentChecked = licensees.includes(option.value)
                            setValue(
                              `licenseUsages.${selectedRow.index}.licensees`,
                              currentChecked ? _.without(licensees, option.value) : _.concat(licensees, option.value),
                              { shouldValidate: true }
                            )
                          }}
                          dense
                        >
                          <ListItemIcon>
                            <Checkbox
                              disabled={selectedRow.original.quantity === field.value?.length && !field.value?.includes(option.value)}
                              edge="start"
                              checked={!!field.value?.includes(option.value)}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText primary={option.label} />
                        </ListItemButton>
                      )}
                    />
                  )
                })}
              </List>
            </Box>
          </Stack>
        )}
      </Drawer>
    </>
  )
}
